import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';

import { authService } from '../services/auth';
import type { User } from '../types';
import { AuthContext } from './auth';


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('sampleflow:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('sampleflow:unauthorized', handleUnauthorized);
  }, []);

  const completeFeishuLogin = useCallback(async (code: string, state: string) => {
    const loggedInUser = await authService.completeFeishuLogin(code, state);
    setUser(loggedInUser);
    return loggedInUser;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    refreshUser,
    completeFeishuLogin,
    logout
  }), [user, loading, refreshUser, completeFeishuLogin, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
